import { useNavigate } from 'react-router-dom';
import { BreadcrumbNav, Button, Skeleton, StatusMessage, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Pagination } from '../../components/uiChrome.jsx';
import { AuditTable } from '../../components';
import { useGlobalAudit } from '../../hooks/audit';
import GlobalAuditFilters from './GlobalAuditFilters.jsx';
import { auditToCsv } from '../../utils';
import { downloadTextFile } from '../../../../shared/utils/downloadTextFile';
import { AlertTriangle, ArrowLeft, Blocks, Download } from 'lucide-react';

const PAGE_WRAP = 'flex min-h-0 flex-1 flex-col bg-surface-base';
const PAGE_INNER = 'px-6';

/**
 * Global Audit log (CT-4210 + CT-4211 + CT-4212 + CT-4213). Full-width chrome
 * matching Distribution: title + export actions, the dedicated
 * {@link GlobalAuditFilters} bar, the shared {@link AuditTable}, and a pager
 * pinned at the bottom. Query-string params keep `?provider=` / `?instance=`
 * deep links; when one is active a "← Back to {provider}/instance" action
 * returns to the originating detail page (CT-4213). Expanding a row reveals the
 * full read-only detail (before/after state, actor, IP); Export CSV downloads
 * the currently-filtered rows client-side (CT-4212).
 */
const GlobalAuditPage = () => {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const toast = useToast();
  const {
    items,
    total,
    totalPages,
    page,
    perPage,
    now,
    isLoading,
    error,
    filters,
    setSearch,
    setDateRange,
    setSeverities,
    setProvider,
    setInstance,
    applyFilters,
    setPage,
    setPerPage,
    clearFilters,
    exportRows,
  } = useGlobalAudit();

  /**
   * Build a CSV of the full filtered result set (all pages, not just the visible
   * one) and trigger a client-side download (CT-4212).
   */
  const handleExportCsv = async () => {
    toast.info({ message: t('integrations.globalAudit.exportCsvToast'), canClose: true });
    const rows = await exportRows();
    downloadTextFile('audit-log.csv', auditToCsv(rows));
  };

  const rangeLabel =
    total === 0 ? '0' : `${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)}`;

  const hasActiveFilters = Boolean(
    filters.search.trim() ||
      filters.dateRange.length ||
      filters.severities.length ||
      filters.provider ||
      filters.instance ||
      filters.actor ||
      filters.code,
  );
  const emptyCopy = hasActiveFilters
    ? { title: 'integrations.globalAudit.empty', hint: 'integrations.globalAudit.emptyHint' }
    : { title: 'integrations.globalAudit.emptyNone', hint: 'integrations.globalAudit.emptyNoneHint' };

  return (
    <main className={PAGE_WRAP}>
      <div className={`${PAGE_INNER} flex-1 overflow-y-auto pb-4 pt-3.5 scrollbar-aimsos-auto-sm`}>
        <BreadcrumbNav
          size="sm"
          items={[{ label: t('integrations.globalAudit.title'), path: '.' }]}
          onNavigate={(path) => navigate(path)}
        />

        {/* Back navigation to the originating provider/instance when the view
            arrived pre-filtered via a deep link (CT-4213). */}
        {(filters.provider || filters.instance) && (
          <div className="mt-2 flex flex-wrap gap-2">
            {filters.provider && (
              <Button
                variant="tertiary"
                size="sm"
                leftIcon={ArrowLeft}
                onClick={() => navigate(`/admin-studio/integrations/${filters.provider}`)}
              >
                {t('integrations.globalAudit.backToProvider', { provider: filters.provider })}
              </Button>
            )}
            {filters.provider && filters.instance && (
              <Button
                variant="tertiary"
                size="sm"
                leftIcon={ArrowLeft}
                onClick={() =>
                  navigate(`/admin-studio/integrations/${filters.provider}/instances/${filters.instance}`)
                }
              >
                {t('integrations.globalAudit.backToInstance')}
              </Button>
            )}
          </div>
        )}

        <div className="mb-4 mt-4 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-[-0.015em] text-content">
              {t('integrations.globalAudit.title')}
            </h1>
            <p className="mt-1 text-[13px] text-content-secondary">{t('integrations.globalAudit.subtitle')}</p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => toast.info({ message: t('integrations.globalAudit.webhookToast'), canClose: true })}
            >
              {t('integrations.globalAudit.configureWebhook')}
            </Button>
            <Button variant="primary" size="sm" leftIcon={Download} onClick={handleExportCsv}>
              {t('integrations.globalAudit.exportCsv')}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : error ? (
          <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />
        ) : (
          <div className="flex flex-col gap-3">
            <GlobalAuditFilters
              filters={filters}
              setSearch={setSearch}
              setDateRange={setDateRange}
              setSeverities={setSeverities}
              setProvider={setProvider}
              setInstance={setInstance}
              applyFilters={applyFilters}
              clearFilters={clearFilters}
            />

            {items.length === 0 ? (
              <StatusMessage
                icon={Blocks}
                title={t(emptyCopy.title)}
                subtitle={t(emptyCopy.hint)}
              />
            ) : (
              <AuditTable
                rows={items}
                now={now}
                slug="workspace"
                emptyLabel={t('integrations.audit.empty')}
              />
            )}
          </div>
        )}
      </div>

      {!isLoading && !error && items.length > 0 ? (
        <div className={`${PAGE_INNER} shrink-0 py-3`}>
          <Pagination
            className="shrink-0"
            rangeLabel={rangeLabel}
            totalItems={total}
            currentPage={page}
            totalPages={totalPages}
            perPage={perPage}
            hasPrevious={page > 1}
            hasNext={page < totalPages}
            onPrevPage={() => setPage(Math.max(1, page - 1))}
            onNextPage={() => setPage(Math.min(totalPages, page + 1))}
            onPageSizeChange={setPerPage}
          />
        </div>
      ) : null}
    </main>
  );
};

export default GlobalAuditPage;
